import { useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Badge } from 'components/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';
import {
  CompetenciesCard,
  CompetenciesCardLevel
} from 'components/competencies';

import type { CompetencyInProduct } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  badgeIcon?: IconProp;
  badgeIconStyle?: React.CSSProperties;
  competency: CompetencyInProduct;
  includeAvailableLevels?: boolean;
}

const CompetenciesProductBadge = ({
  badgeIcon = 'medal',
  badgeIconStyle,
  competency,
  includeAvailableLevels
}: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(undefined);

  const { language, messages, theme } = useReduxState();

  const [modalVisible, setModalVisible] = useState(false);

  const hideModal = () => {
    setModalVisible(false);
    setTimeout(() => {
      buttonRef.current?.focus();
    });
  };

  const showModal = () => {
    setModalVisible(true);
  };

  return (
    <>
      <button className="button" ref={buttonRef} type="button">
        <Badge
          category="default"
          icon={badgeIcon}
          iconStyle={badgeIconStyle}
          isRounded={false}
          onClick={showModal}
          text={
            <span className="inner">
              {competency.nameLocales?.[language] || competency.name}
              {competency.level ? (
                <span>
                  {': '}
                  <span style={{ fontWeight: 400 }}>
                    {competency.level.nameLocales?.[language] ||
                      competency.level.name}
                  </span>
                </span>
              ) : null}
            </span>
          }
        />

        <style jsx>{`
          .button {
            background-color: transparent;
            border: 0;
            cursor: pointer;
            outline: 0;
            font-family: inherit;
            padding: 0;
          }
          .inner {
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          @media (min-width: ${breakpoints.small}px) {
            .inner {
              max-width: 320px;
            }
          }
        `}</style>
      </button>

      <ModalPortal>
        <Modal
          closeButton={true}
          onHide={hideModal}
          title={messages.commonCompetence}
          visible={modalVisible}
          width="medium"
        >
          <ModalContent style={{ padding: 0 }}>
            <div className="competency">
              <CompetenciesCard
                description={
                  competency.descriptionLocales?.[language] ||
                  competency.description
                }
                id={competency.id}
                isChecked={includeAvailableLevels ? false : !competency.level}
                isCheckedPending={
                  includeAvailableLevels ? false : !competency.level
                }
                name={competency.nameLocales?.[language] || competency.name}
              >
                {includeAvailableLevels &&
                competency.availableLevels.length > 0 ? (
                  <div className="levels">
                    {competency.availableLevels.map((level) => {
                      return (
                        <CompetenciesCardLevel
                          key={level.id}
                          competencyId={competency.id}
                          description={
                            level.descriptionLocales?.[language] ||
                            level.description
                          }
                          id={level.id}
                          isChecked={false}
                          name={level.nameLocales?.[language] || level.name}
                        />
                      );
                    })}
                  </div>
                ) : competency.level ? (
                  <CompetenciesCardLevel
                    key={competency.level.id}
                    competencyId={competency.id}
                    description={
                      competency.level.descriptionLocales?.[language] ||
                      competency.level.description
                    }
                    id={competency.level.id}
                    isChecked={true}
                    isCheckedPending={true}
                    name={
                      competency.level.nameLocales?.[language] ||
                      competency.level.name
                    }
                  />
                ) : null}
              </CompetenciesCard>

              <style jsx>{`
                .competency {
                  background-color: ${theme.lightGreyLighter};
                  padding: 15px;
                }
              `}</style>
            </div>
          </ModalContent>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default CompetenciesProductBadge;
